import React, { useState } from 'react';
import { PlanStep, PlanStepStatus } from '../../types/capstone';

interface Props {
  plan: PlanStep[] | null;
  busy?: boolean;
  busySeconds?: number;
}

const STATUS_BADGE: Record<PlanStepStatus, { label: string; classes: string }> = {
  pending: {
    label: '○',
    classes: 'text-gray-400 dark:text-gray-500 border-gray-300 dark:border-gray-600',
  },
  in_progress: {
    label: '◐',
    classes: 'text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-700 animate-pulse',
  },
  done: {
    label: '●',
    classes: 'text-green-600 dark:text-green-400 border-green-300 dark:border-green-700',
  },
  failed: {
    label: '✕',
    classes: 'text-red-600 dark:text-red-400 border-red-300 dark:border-red-700',
  },
};

// Collapsible rollup: a compact "Plan · 2/4" bar that expands to the full
// checklist. Default collapsed so it doesn't eat vertical space.
const PlanChecklist: React.FC<Props> = ({ plan, busy = false, busySeconds = 0 }) => {
  const [open, setOpen] = useState(false);

  const workingIndicator = busy ? (
    <span className="text-xs text-purple-600 dark:text-purple-300 animate-pulse shrink-0 not-italic">
      working {busySeconds}s…
    </span>
  ) : null;

  if (!plan || plan.length === 0) {
    return (
      <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 italic flex items-center gap-2">
        <span>Waiting for plan — AI will propose one once the project shape is clear.</span>
        {workingIndicator}
      </div>
    );
  }

  const done = plan.filter((s) => s.status === 'done').length;
  const allDone = done === plan.length;
  const current =
    plan.find((s) => s.status === 'in_progress') || plan.find((s) => s.status === 'pending');

  return (
    <div className="px-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 py-2 text-sm text-left cursor-pointer hover:opacity-80"
        aria-expanded={open}
        title={open ? 'Collapse plan' : 'Expand plan'}
      >
        <span className="text-purple-600 dark:text-purple-300 text-[11px] w-3 shrink-0">{open ? '▼' : '▶'}</span>
        <span className="font-medium text-gray-700 dark:text-gray-200">Plan</span>
        <span
          className={`text-xs px-1.5 py-0.5 rounded-full ${
            allDone
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
          }`}
        >
          {done}/{plan.length}
        </span>
        {!open && !allDone && current && (
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">· {current.title}</span>
        )}
        {!open && allDone && (
          <span className="text-xs text-green-600 dark:text-green-400">· all steps done</span>
        )}
        {workingIndicator}
      </button>

      {open && (
        <ol className="pb-3 space-y-2">
          {plan.map((step) => {
            const badge = STATUS_BADGE[step.status] ?? STATUS_BADGE.pending;
            return (
              <li key={step.id} className="flex items-start gap-3 text-sm">
                <span
                  className={`inline-flex items-center justify-center w-6 h-6 rounded-full border ${badge.classes} font-mono shrink-0`}
                  aria-label={step.status}
                  title={step.status}
                >
                  {badge.label}
                </span>
                <span className="flex-1 text-gray-800 dark:text-gray-200">
                  <span className="font-medium text-gray-500 dark:text-gray-400 mr-2">{step.id}.</span>
                  {step.title}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default PlanChecklist;
